import React, { useState, useEffect } from "react";

import { Box, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import ModernModal from "../ModernModal";
import { pricingPlanService } from "./services";
import { PricingPlan } from "./types";
import PricingPlanCard from "./components/PricingPlanCard";
import { trackEvent } from "../../../analytics";

interface Props {
  handleOpenPricingPlanDialog: () => void;
  open: boolean;
}

export default function PricingPlaneDialog({
  handleOpenPricingPlanDialog,
  open,
}: Props) {
  const [plan, setPlan] = useState("monthly");

  useEffect(() => {
    if (open) {
      trackEvent("pricing_viewed");
    }
  }, [open]);

  const { data: pricingPlans, isLoading } = useQuery({
    queryKey: ["getBillingPlans"],
    queryFn: () => pricingPlanService.getPricingPlans(),
  });

  const formatUsd = (cents: number) =>
    (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });

  /** Whole dollars, no cents — for yearly totals and monthly equivalent. */
  const formatUsdWhole = (dollars: number) =>
    Math.round(dollars).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const getPricesByInterval = (plans: PricingPlan[], interval: string) => {
    return plans.map((planItem: PricingPlan) => {
      const priceDetail = planItem.prices.find(
        (price) => price.recurring_interval === interval,
      );
      if (!priceDetail) {
        return {
          id: planItem.id,
          name: planItem.name,
          price_in_cents: null,
          price_highlight: null,
          price_period_label: null,
          annual_billing_summary: null,
          description: planItem.description,
          credits_per_month: planItem.credits_per_month,
          recurring_interval: undefined,
          features: planItem.features,
          is_allowed_to_purchase: undefined,
          is_popular: false,
        };
      }

      const cents = priceDetail.price_in_cents;
      const yearlyTotalDollars = cents / 100;
      const yearlyTotalFormatted = formatUsdWhole(yearlyTotalDollars);

      // Yearly Stripe price = amount charged per year; show monthly equivalent + total.
      if (interval === "yearly") {
        const monthlyDollarsRounded = Math.round(cents / 12 / 100);
        const monthlyEquivalentFormatted = formatUsdWhole(
          monthlyDollarsRounded,
        );
        return {
          id: planItem.id,
          name: planItem.name,
          price_in_cents: monthlyEquivalentFormatted,
          price_highlight: monthlyEquivalentFormatted,
          price_period_label: "/month",
          annual_billing_summary: `Billed annually (${yearlyTotalFormatted}/year)`,
          description: planItem.description,
          credits_per_month: planItem.credits_per_month,
          recurring_interval: priceDetail.recurring_interval,
          features: planItem.features,
          is_allowed_to_purchase: priceDetail.is_allowed_to_purchase,
          is_popular: false,
        };
      }

      const monthlyFormatted = formatUsd(cents);
      return {
        id: planItem.id,
        name: planItem.name,
        price_in_cents: monthlyFormatted,
        price_highlight: monthlyFormatted,
        price_period_label: "/month",
        annual_billing_summary: null,
        description: planItem.description,
        credits_per_month: planItem.credits_per_month,
        recurring_interval: priceDetail.recurring_interval,
        features: planItem.features,
        is_allowed_to_purchase: priceDetail.is_allowed_to_purchase,
        is_popular: false,
      };
    });
  };

  const pricingPlansData = getPricesByInterval(pricingPlans || [], plan);

  return (
    <ModernModal
      open={open}
      onClose={handleOpenPricingPlanDialog}
      title="Upgrade Your Plan"
      maxWidth="xl"
      paperSx={{ maxHeight: "94vh" }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto", pb: 2 }}>
        {/* Billing cycle toggle */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box
            sx={{
              display: "inline-flex",
              backgroundColor: "grey.100",
              borderRadius: "10px",
              p: "4px",
              gap: "4px",
            }}
          >
            {(["monthly", "yearly"] as const).map((cycle) => {
              const active = plan === cycle;
              return (
                <Box
                  key={cycle}
                  component="button"
                  onClick={() => setPlan(cycle)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    px: 2,
                    py: "7px",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: "7px",
                    fontSize: "13px",
                    fontWeight: active ? 700 : 500,
                    color: active ? "text.primary" : "text.secondary",
                    backgroundColor: active ? "#fff" : "transparent",
                    boxShadow: active ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                    transition: "all 0.15s ease",
                    fontFamily: "inherit",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: active ? "#fff" : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  {cycle === "monthly" ? "Monthly" : "Annual"}
                  {cycle === "yearly" && (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: "#dcfce7",
                        color: "#16a34a",
                        border: "1px solid #bbf7d0",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 700,
                        px: "6px",
                        py: "2px",
                        lineHeight: 1.4,
                      }}
                    >
                      Save 30%
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: "#2563eb" }} />
          </Box>
        ) : (
          <PricingPlanCard pricingPlansData={pricingPlansData} />
        )}
      </Box>
    </ModernModal>
  );
}
